import React from 'react';
import { motion } from 'framer-motion';
import { Megaphone } from 'lucide-react';
import BannerDisplay from '../advertising/BannerDisplay';
import { AD_POOL_LIMIT, AD_ROTATION_SECONDS } from '../advertising/constants';
import { useAdSlots } from '../advertising/useAdSlots';

interface RightSidebarProps {
  /** When true, renders content only (parent handles the aside + scroll) */
  embedded?: boolean;
}

const SIDEBAR_AD_SLOTS = ['sidebar_top', 'sidebar_middle', 'sidebar_bottom'] as const;

const RightSidebar: React.FC<RightSidebarProps> = ({ embedded = false }) => {
  const { adsBySlot, loading } = useAdSlots(SIDEBAR_AD_SLOTS, {
    limit: AD_POOL_LIMIT,
  });

  const content = (
    <div className="space-y-5 pb-4">
      {/* Top Banner Ad */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1e2a5e] overflow-hidden"
      >
        <div className="flex items-center gap-2 p-2.5 border-b border-gray-200 dark:border-[#1e2a5e]">
          <div className="p-1.5 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg">
            <Megaphone className="w-3 h-3 text-white" />
          </div>
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sponsored</span>
        </div>
        <div className="p-2">
          <BannerDisplay
            placement="sidebar_top"
            width="100%"
            height={240}
            rotationInterval={AD_ROTATION_SECONDS}
            limit={AD_POOL_LIMIT}
            ads={adsBySlot.sidebar_top}
            loading={loading}
          />
        </div>
      </motion.div>

      {/* Middle Banner Ad */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1e2a5e] overflow-hidden"
      >
        <div className="flex items-center gap-2 p-2.5 border-b border-gray-200 dark:border-[#1e2a5e]">
          <div className="p-1.5 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg">
            <Megaphone className="w-3 h-3 text-white" />
          </div>
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sponsored</span>
        </div>
        <div className="p-2">
          <BannerDisplay
            placement="sidebar_middle"
            width="100%"
            height={240}
            rotationInterval={AD_ROTATION_SECONDS}
            limit={AD_POOL_LIMIT}
            ads={adsBySlot.sidebar_middle}
            loading={loading}
          />
        </div>
      </motion.div>

      {/* Bottom Banner Ad */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-gray-50 dark:bg-[#0d1333] rounded-2xl shadow-sm border border-gray-200 dark:border-[#1e2a5e] overflow-hidden"
      >
        <div className="flex items-center gap-2 p-2.5 border-b border-gray-200 dark:border-[#1e2a5e]">
          <div className="p-1.5 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-lg">
            <Megaphone className="w-3 h-3 text-white" />
          </div>
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sponsored</span>
        </div>
        <div className="p-2">
          <BannerDisplay
            placement="sidebar_bottom"
            width="100%"
            height={240}
            rotationInterval={AD_ROTATION_SECONDS}
            limit={AD_POOL_LIMIT}
            ads={adsBySlot.sidebar_bottom}
            loading={loading}
          />
        </div>
      </motion.div>
    </div>
  );

  if (embedded) return content;

  return (
    <aside className="w-72 flex-shrink-0 hidden xl:block">
      <div className="sticky top-20 max-h-[calc(100vh-5rem)] overflow-y-auto scrollbar-thin space-y-6">
        {content}
      </div>
    </aside>
  );
};

export default RightSidebar;
